<script setup>
import { computed } from 'vue';

import CaptainGenerationDetails from '../CaptainGenerationDetails.vue';
import Icon from 'next/icon/Icon.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION, SENDER_TYPES } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  id,
  sender,
  senderType,
  isPrivate,
} = useMessageContext();
const { t } = useI18n();

const isCaptainMessage = computed(
  () =>
    (sender.value?.type ?? senderType.value) === SENDER_TYPES.CAPTAIN_ASSISTANT
);

const metaColorClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.PRIVATE
    ? 'text-n-amber-12/50'
    : 'text-n-slate-11'
);

const emailMetaClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : ''
);

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

// BonBoZ: góc dưới chỉ thu về 4px khi còn tin cùng cụm phía dưới; Chatwoot thu luôn.
const orientationMap = computed(() => ({
  [ORIENTATION.LEFT]: [
    'left-bubble rounded-2xl',
    shouldGroupWithNext.value
      ? 'ltr:rounded-bl-[4px] rtl:rounded-br-[4px]'
      : '',
  ],
  [ORIENTATION.RIGHT]: [
    'right-bubble rounded-2xl',
    shouldGroupWithNext.value
      ? 'ltr:rounded-br-[4px] rtl:rounded-bl-[4px]'
      : '',
  ],
  [ORIENTATION.CENTER]: ['rounded-md'],
}));

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap.value[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

// BonBoZ: chân bong bóng không còn giờ gửi lẫn dấu trạng thái — giờ nằm trong
// tooltip khi rê chuột, dấu "đã gửi" nằm ngoài bong bóng ở cột bên phải. Bỏ hai
// thứ đó đi thì bong bóng thu lại đúng kiểu Pancake.
const shouldShowFooter = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

// Ổ khoá là dấu duy nhất phân biệt ghi chú nội bộ với tin đã gửi cho khách, nên
// nó ở lại chân bong bóng thay vì đi theo giờ vào tooltip — nhầm chỗ này là gửi
// nhầm ghi chú nội bộ cho khách.
const shouldShowLock = computed(
  () => shouldShowFooter.value && isPrivate.value
);

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <slot />
    <CaptainGenerationDetails
      v-if="shouldShowFooter && isCaptainMessage"
      :message-id="id"
      :class="emailMetaClass"
      class="mt-2"
    />
    <div
      v-if="shouldShowLock"
      class="flex mt-1"
      :class="[flexOrientationClass, emailMetaClass, metaColorClass]"
    >
      <Icon icon="i-lucide-lock-keyhole" class="size-3" />
    </div>
  </div>
</template>
